import { useState, useEffect, useRef } from "react"
import PlusIcon from "@assets/icons/plus"
import { SearchIcon } from "@assets/icons/Search"
import { Button } from "@components/Button"
import { DISORDERS_LIST } from "../../ViewPatientPage/helper"
import usePatient from "@hooks/usePatient"
import { DeleteDisorder } from "../DeleteDisorder"

const DiagnosisSection = ({ patientData, patientId, refreshPatientData }) => {
  const { handleAddDiagnosis } = usePatient()
  const [diagnosis, setDiagnosis] = useState("")
  const [isAddingDiagnosis, setIsAddingDiagnosis] = useState(false)
  const [searchDiagnosis, setSearchDiagnosis] = useState("")
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [customDiagnosis, setCustomDiagnosis] = useState("")
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null)
  const dropdownRef = useRef(null)

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowSuggestions(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  // Search through disorders list
  useEffect(() => {
    if (searchDiagnosis.trim() === "") {
      setSuggestions([])
      setShowSuggestions(false)
      return
    }

    const results = []
    const searchLower = searchDiagnosis.toLowerCase()

    Object.entries(DISORDERS_LIST).forEach(([category, disorders]) => {
      disorders.forEach((disorder) => {
        if (disorder.toLowerCase().includes(searchLower)) {
          results.push({ category, disorder })
        }
      })
    })

    setSuggestions(results)
    setShowSuggestions(true)

    if (results.length === 0) {
      setCustomDiagnosis(searchDiagnosis)
    }
  }, [searchDiagnosis])

  const handleSearchChange = (event) => {
    setSearchDiagnosis(event.target.value)
  }

  const selectDiagnosis = (disorder) => {
    // Instead of setting to local state, call API directly when suggestion is selected
    setIsAddingDiagnosis(true)
    handleAddDiagnosis(
      {
        patientId,
        diagnosed: disorder,
      },
      onAddDiagnosisSuccess
    )
  }

  const onAddDiagnosisSuccess = () => {
    refreshPatientData()
    setSearchDiagnosis("")
    setDiagnosis("") // Reset to empty string
    setIsAddingDiagnosis(false)
  }

  const onAddDiagnosisSubmit = () => {
    if (searchDiagnosis.trim() !== "") {
      // Submit whatever is in the search box
      setIsAddingDiagnosis(true)
      handleAddDiagnosis(
        {
          patientId,
          diagnosed: searchDiagnosis.trim(),
        },
        onAddDiagnosisSuccess
      )
    } else if (diagnosis) {
      // If there's a selected diagnosis, submit that
      setIsAddingDiagnosis(true)
      handleAddDiagnosis(
        {
          patientId,
          diagnosed: diagnosis,
        },
        onAddDiagnosisSuccess
      )
    }
  }

  // Function to handle delete diagnosis click
  const handleDeleteClick = (diagnosisItem) => {
    setSelectedDiagnosis(diagnosisItem)
    setShowDeleteDialog(true)
  }

  // Function to close the delete dialog
  const handleCloseDeleteDialog = () => {
    setShowDeleteDialog(false)
    setSelectedDiagnosis(null)
  }

  // Function to handle successful deletion
  const handleDeleteSuccess = () => {
    refreshPatientData()
    setShowDeleteDialog(false)
    setSelectedDiagnosis(null)
  }

  return (
    <>
      <div className="py-10 px-8 rounded-2xl bg-[--primary-blue-light] flex flex-col gap-4">
        {/* Restructured for better mobile display */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          {/* Title section - always on top/left */}
          <div className="flex items-center gap-2">
            <PlusIcon stroke="var(--primary-blue)" />
            <div className="text-xs text-[--text-light-gray] font-bold tracking-widest">
              ADD NEW DIAGNOSIS
            </div>
          </div>

          {/* Search section - full width on mobile, right-aligned on desktop */}
          <div className="flex items-center w-full md:w-auto gap-2">
            <div
              className="relative flex-grow md:flex-grow-0"
              ref={dropdownRef}
            >
              <input
                type="text"
                className="pl-4 pr-10 py-2 rounded-lg border border-[--primary-blue] focus:outline-none w-full md:w-64"
                placeholder="Add Disorder"
                value={searchDiagnosis}
                onChange={handleSearchChange}
              />
              <SearchIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />

              {/* Improved Suggestions dropdown */}
              {showSuggestions && (
                <div className="absolute z-10 mt-1 w-full md:w-72 bg-white border border-gray-200 rounded-md shadow-lg max-h-60 overflow-y-auto left-0">
                  {suggestions.length > 0 ? (
                    suggestions.map((item, index) => (
                      <div
                        key={index}
                        className="px-4 py-3 hover:bg-blue-100 cursor-pointer"
                        onClick={() => selectDiagnosis(item.disorder)}
                      >
                        <span className="font-medium text-gray-800 block">
                          {item.disorder}
                        </span>
                      </div>
                    ))
                  ) : (
                    <div className="px-4 py-3 text-sm text-gray-500">
                      No matches found. Click "Add" to add custom diagnosis.
                    </div>
                  )}
                </div>
              )}
            </div>
            <Button
              variant="transparent"
              type="button"
              className="px-4 py-2 !bg-[--pure-white] !text-[--primary-blue] border border-[--primary-blue] whitespace-nowrap"
              onClick={onAddDiagnosisSubmit}
              loading={isAddingDiagnosis}
            >
              ADD
            </Button>
          </div>
        </div>

        <div className="flex flex-col md:flex-row gap-16">
          <div className="p-4 rounded-2xl bg-[--pure-white] h-[12rem] grow md:border border-[--primary-blue] overflow-y-auto">
            {patientData?.local?.diagnosed &&
            patientData.local.diagnosed.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {patientData.local.diagnosed.map((diagnosisItem, index) => (
                  <div
                    key={index}
                    className={`inline-flex items-center rounded-md px-3 py-1 text-sm relative ${
                      diagnosisItem.isDeleted
                        ? "bg-[#BCBEC0] border border-[#BCBEC0] opacity-75"
                        : "bg-[#E6F5FF] hover:bg-[#FBEEFF] border border-[#C4E2FF] group"
                    }`}
                  >
                    {diagnosisItem.isDeleted ? (
                      <div
                        className="mr-2 text-2xl w-4 h-4 flex items-center justify-center"
                        style={{ lineHeight: 1 }}
                      ></div>
                    ) : (
                      <button
                        className="mr-2 text-red-400 hover:text-red-600 text-2xl font-bold flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={() => handleDeleteClick(diagnosisItem)}
                        type="button"
                        style={{ lineHeight: 1 }}
                      >
                        ×
                      </button>
                    )}

                    <span
                      style={{
                        fontFamily: "Manrope",
                        fontWeight: 700,
                        fontSize: "12px",
                        lineHeight: "16px",
                        letterSpacing: "1.5px",
                        textTransform: "uppercase",
                        textDecoration: diagnosisItem.isDeleted
                          ? "line-through"
                          : "none",
                      }}
                    >
                      {diagnosisItem.name} |{" "}
                      {new Date(diagnosisItem.date).toLocaleDateString()}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-gray-400">No Diagnosis added</div>
            )}
          </div>
        </div>
      </div>

      {/* Render DeleteDisorder dialog conditionally */}
      {showDeleteDialog && selectedDiagnosis && (
        <DeleteDisorder
          selectedExercisesToDelete={[selectedDiagnosis.name]}
          onClose={handleCloseDeleteDialog}
          onSuccess={handleDeleteSuccess}
          diagnosisId={selectedDiagnosis._id}
          patientId={patientId}
        />
      )}
    </>
  )
}

export default DiagnosisSection
